<script setup>
import { computed, inject, toValue } from 'vue';
import { useField, FormContextKey } from 'vee-validate';

const props = defineProps({
    // Classes da coluna em volta do campo.
    classeColuna: {
        type: String,
        default: 'col-12'
    },
    // Nome do campo no schema do useForm. Quando informado, o componente se conecta
    // sozinho ao formulário pai (valor e erro), dispensando v-model e :erro.
    name: {
        type: String,
        default: null
    },
    erro: {
        type: String,
        default: null
    },
    modelValue: {
        type: [Boolean, String, Number],
        default: null
    },
    titulo: {
        type: String,
        required: true
    },
    descricao: {
        type: String,
        default: ''
    },
    // Lista de { rotulo, valor }. O padrão é Sim/Não com valores booleanos.
    opcoes: {
        type: Array,
        default: () => [
            { rotulo: 'Sim', valor: true },
            { rotulo: 'Não', valor: false }
        ]
    },
    disabled: {
        type: Boolean,
        default: false
    },
    // Normalmente dispensável: a obrigatoriedade vem do schema. Serve para forçar o
    // asterisco em regra condicional (`when`), que o schema não expõe estaticamente.
    obrigatorio: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'change']);

const campoForm = props.name ? useField(() => props.name) : null;

const selecionado = computed({
    get: () => (campoForm ? campoForm.value.value : props.modelValue) ?? null,
    set: (value) => {
        if (campoForm) campoForm.value.value = value;
        emit('update:modelValue', value);
        emit('change', value);
    }
});

const mensagemErro = computed(() => props.erro ?? campoForm?.errorMessage.value ?? null);

const formulario = inject(FormContextKey, null);

// O asterisco de obrigatório sai da própria regra do schema (ex.: regraSelectButton() vs
// regraSelectButton({ obrigatorio: false })), para não ser declarado duas vezes.
const obrigatorioDoSchema = computed(() => {
    if (!props.name) return null;

    try {
        const campo = toValue(formulario?.schema)?.describe?.()?.fields?.[props.name];
        return campo ? campo.optional === false : null;
    } catch {
        return null;
    }
});

// O schema só acrescenta o asterisco: a prop continua vencendo, porque um `when`
// aparece como opcional no describe() e ainda assim é obrigatório em tempo de uso.
const obrigatorio = computed(() => props.obrigatorio || obrigatorioDoSchema.value === true);
</script>

<template>
    <div class="field" :class="props.classeColuna">
        <div class="mb-2 border-1 border-round-2xl flex flex-column xl:flex-row gap-3 justify-content-between p-5"
            :class="mensagemErro ? 'border-red-500' : 'border-300'">
            <div class="flex flex-column gap-2">
                <span class="text-lg font-semibold text-900">
                    {{ props.titulo }}<span v-if="obrigatorio" class="text-red-500 ml-1">*</span>
                </span>
                <span v-if="props.descricao" class="text-500 text-xs w-18rem">{{ props.descricao }}</span>
            </div>
            <SelectButton v-model="selecionado" :options="props.opcoes" optionLabel="rotulo" optionValue="valor"
                :disabled="props.disabled" :allowEmpty="false" :pt="{ root: { class: 'h-2rem' } }" />
        </div>
        <SmallError v-if="mensagemErro" :error="mensagemErro" />
    </div>
</template>
